import React from 'react';
import withStyles from '@mui/styles/withStyles';
import Button from '@mui/material/Button';
import cx from 'classnames';
import { joinDefined } from '../../util/strings';

const styles = ({ funeralHome, palette, breakpoints }) => ({
    notModal: {},
    buttonLabel: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'inherit',
        textTransform: 'capitalize',
        fontWeight: 'normal',
        fontSize: '0.9rem',
        lineHeight: 'normal',
        gap: '6px'
    },
    addButtonDesktop: {
        boxShadow: 'none',
        borderRadius: 30,
        padding: '0.15rem 1.5rem',
        fontSize: '0.9em',
        fontWeight: '300',
        minWidth: '8rem',
        minHeight: '2.65rem',
        backgroundColor: palette.basePrimary[funeralHome],
        backgroundImage: `radial-gradient(circle at 24px 50%, #ffffff22, transparent 48px)`,
        border: '1.5px solid ' + palette.basePrimary[funeralHome],
        '&:hover': {
            backgroundColor: palette.baseSecondary[funeralHome] + 'AA',
            border: '1.5px solid ' + palette.baseSecondary[funeralHome] + 'AA'
        },
        '& svg, & .material-icons': {
            [breakpoints.up('md')]: {
                fontSize: '1rem',
                width: '1rem',
                height: '1rem'
                //marginRight: '0.35rem'
            }
        },
        [breakpoints.down('sm')]: {
            minWidth: '4em'
        }
    },

    altButton: {
        backgroundColor: palette.baseSecondary[funeralHome],
        borderColor: palette.baseSecondary[funeralHome],
        color: palette.button.alt.foregroundColor,
        '&:hover': {
            backgroundColor: 'white',
            borderColor: palette.baseSecondary[funeralHome],
            color: palette.baseSecondary[funeralHome]
        }
    },

    outlineButton: {
        background: 'white',
        color: palette.contentForeground[funeralHome],
        borderColor: palette.basePrimary[funeralHome],
        '&:hover': {
            backgroundColor: palette.basePrimary[funeralHome],
            color: 'white',
            borderColor: palette.basePrimary[funeralHome]
        }
    },

    modalPrimaryButton: {
        backgroundColor: palette.baseSecondary[funeralHome],
        borderColor: palette.baseSecondary[funeralHome],
        color: palette.button.alt.foregroundColor,
        '&:hover': {
            backgroundColor: palette.action.hover,
            borderColor: palette.baseSecondary[funeralHome],
            color: palette.baseSecondary[funeralHome]
        }
    },

    saveButton: {
        backgroundColor: palette.button.save,
        borderColor: palette.button.save,
        '&:hover': {
            borderColor: palette.button.save,
            backgroundColor: palette.action.hover,
            color: palette.button.save
        }
    },
    destroyButton: {
        backgroundColor: palette.button.destroy,
        borderColor: palette.button.destroy,
        '&:hover': {
            borderColour: palette.button.destroy,
            backgroundColor: '#ffffff',
            color: palette.button.destroy
        }
    }
});

/**
 * Primary coloured button, suitable for generic actions. Prepend "+" for adding inline items, append "..." when opening modals.
 */
const PrimaryButton = ({ classes, onClick, text, children, className, color, title, target, forModal, ...props }) => {
    const customColorClass = classes[color];
    if (target) {
        title = joinDefined([title, '(Opens in a new window)'], ' ');
    }
    return (
        <Button
            title={title}
            target={target}
            variant="contained"
            color={'primary'}
            onClick={onClick}
            className={cx(
                classes.addButtonDesktop,
                className,
                customColorClass,
                target && 'targetNewPage',
                !forModal && classes.notModal
            )}
            {...props}
        >
            <span className={classes.buttonLabel}>{children || text}</span>
        </Button>
    );
};

/**
 * Alternatively-styled button for unusual actions eg. printing, sending emails.
 */
export const AltButton = withStyles(styles)(({ classes, className, ...props }) => (
    <PrimaryButton className={cx(classes.altButton, className)} classes={classes} {...props} />
));

/**
 * Button that uses an outline for the style. Typically used when moving away from current screen eg. closing a modal or leaving the page.
 */
export const OutlineButton = withStyles(styles)(({ classes, className, ...props }) => (
    <PrimaryButton className={cx(classes.outlineButton, className)} classes={classes} {...props} />
));

/**
 * Used on a popup modal to indicate confirmation & closing - but not writing to database!
 */
export const ModalPrimaryButton = withStyles(styles)(({ classes, ...props }) => (
    <PrimaryButton className={classes.modalPrimaryButton} classes={classes} {...props} />
));

/*
 * Only use this button if you are writing to the database!
 */
export const SaveButton = withStyles(styles)(({ classes, className, ...props }) => (
    <PrimaryButton className={cx(classes.saveButton, className)} classes={classes} {...props} />
));

/*
 * Only use this button if you are writing to the database!
 */
export const DestroyButton = withStyles(styles)(({ classes, className, ...props }) => (
    <PrimaryButton className={cx(classes.destroyButton, className)} classes={classes} {...props} />
));

export default withStyles(styles)(PrimaryButton);
